import Env, { timezone } from '~/settings/__modules__/utils/Env';
import AutoReplyService from '~/settings/service/AutoReplyService';
import {
  CommonService,
  UserOptionName,
} from '~/settings/service/CommonService';

export { timezone as TIME_ZONE };
export const DEFAULT_TIME = {
  startTime: '22:00',
  endTime: '07:00',
  weekdays: [0, 1, 2, 3, 4, 5, 6],
};

export class AutoReplyBiz {
  static initialState = {};

  static init(isExternal) {
    let result = null;
    if (isExternal) {
      result = AutoReplyBiz.initForExternal();
    } else {
      result = AutoReplyBiz.initForInternal();
    }

    if (result) {
      // The server return value is a GMT time
      result.timeSettings = result.timeSettings?.map((item) => {
        const [startHour, startMin] = [item.startTimeHour, item.startTimeMin];
        const [endHour, endtMin] = [item.endTimeHour, item.endTimeMin];
        const startTime = `${AutoReplyBiz.formatTime(
          startHour,
          'hour',
        )}:${AutoReplyBiz.formatTime(startMin, 'min')}`;

        const endTime = `${AutoReplyBiz.formatTime(
          endHour,
          'hour',
        )}:${AutoReplyBiz.formatTime(endtMin, 'min')}`;

        return {
          startTime,
          endTime,
          weekdays: item.weekdays?.sort(),
          uuid: AutoReplyBiz.uuid(),
        };
      });

      // If the server does not return a value (The default value is a timezone time)
      if (!result.timeSettings || result.timeSettings.length === 0) {
        result.timeSettings = [
          {
            ...DEFAULT_TIME,
            uuid: AutoReplyBiz.uuid(),
          },
        ];
      }

      this.initialState = JSON.parse(JSON.stringify(result));
      return result;
    }
  }

  static initForInternal() {
    let result = CommonService.getOptionValue(
      UserOptionName.Auto_Reply_Internal,
    );
    if (result) {
      try {
        result = JSON.parse(result);
        return result;
      } catch (e) {
        // empty
        console.error(e);
      }
    }
  }

  static initForExternal() {
    let result = CommonService.getOptionValue(UserOptionName.Auto_Reply);
    if (result) {
      try {
        // 兼容3.0服务器返回的数据
        if (
          result.indexOf('status') > -1 &&
          result.indexOf('enable') === -1 &&
          result.indexOf('timeSettings') === -1
        ) {
          result = result.replace(/\n/g, '\\n').replace(/\r/g, '\\r');
        }
        result = JSON.parse(result);

        // 兼容3.0服务器返回的数据 (3.1之后无status属性)
        if (result.status || result.status === false) {
          const temp = {};
          temp.enable = result.status;
          temp.enableHoliday = false;
          temp.content = result.content || '';
          if (
            result.startTimeHour !== undefined &&
            result.startTimeMin !== undefined &&
            result.endTimeHour !== undefined &&
            result.endTimeMin !== undefined
          ) {
            // GDCWEBMSG-5783
            const [startTimeHour, startTimeMin] =
              AutoReplyBiz.getGMTorTimezoneTime(
                result.startTimeHour,
                result.startTimeMin,
                'gmt',
              );
            const [endTimeHour, endTimeMin] = AutoReplyBiz.getGMTorTimezoneTime(
              result.endTimeHour,
              result.endTimeMin,
              'gmt',
            );
            temp.timeSettings = [
              {
                startTimeHour,
                startTimeMin,
                endTimeHour,
                endTimeMin,
                weekdays: [0, 1, 2, 3, 4, 5, 6],
              },
            ];
          }
          result = temp;
        }
        return result;
      } catch (e) {
        // empty
        console.error(e);
      }
    }
  }

  static checkIsChange(state) {
    const { enable, enableHoliday, timeSettings, content } = state;
    const initialState = AutoReplyBiz.initialState;
    let isChange =
      enable !== initialState.enable ||
      enableHoliday !== initialState.enableHoliday ||
      content !== initialState.content;

    const initTimeSettings = initialState.timeSettings;
    let isTimeChange =
      timeSettings.length !== initTimeSettings.length ||
      timeSettings.some((item, i) => {
        return (
          item.startTime !== initTimeSettings[i].startTime ||
          item.endTime !== initTimeSettings[i].endTime ||
          item.weekdays.join('') !== initTimeSettings[i].weekdays.join('')
        );
      });
    return isChange || isTimeChange;
  }

  static async save(option, isExternal) {
    // 关闭自动回复，不应保存其他页面上已修改但未保存到服务器的项
    if (option.enable === false) {
      option = { ...AutoReplyBiz.initialState, enable: false };
    }
    const timeSettings = option.timeSettings.map((item) => {
      const [startTimeHour, startTimeMin] = item.startTime.split(':');
      const [endTimeHour, endTimeMin] = item.endTime.split(':');
      return {
        startTimeHour: +startTimeHour,
        startTimeMin: +startTimeMin,
        endTimeHour: +endTimeHour,
        endTimeMin: +endTimeMin,
        weekdays: item.weekdays,
      };
    });

    const params = {
      ...option,
      timeSettings,
      enableType: isExternal ? 0 : 1,
    };

    const success = await AutoReplyService.saveAutoReply(params);

    if (success) {
      AutoReplyBiz.initialState = JSON.parse(JSON.stringify(option));
      CommonService.setOptionValue(
        isExternal
          ? UserOptionName.Auto_Reply
          : UserOptionName.Auto_Reply_Internal,
        JSON.stringify(params),
      );
    }
    return !!success;
  }

  static getGMTorTimezoneTime(hour, minute, timetype) {
    const offset = Env.getOffset();
    let date = new Date();
    date.setHours(hour);
    date.setMinutes(minute);
    const timestamp =
      timetype === 'gmt'
        ? date.getTime() + offset * 60 * 1000
        : date.getTime() - offset * 60 * 1000;

    let time = new Date(timestamp);
    return [time.getHours(), time.getMinutes()];
  }

  static getGMTOffset() {
    const found = Env.getGMTOffsetObj();
    if (!found) return '';
    return `${found.name} ${found.gmt}`;
  }

  static formatTime(time, type) {
    if (type === 'hour') {
      time = time >= 23 ? 23 : time;
    } else if (type === 'min') {
      time = time >= 59 ? 59 : time;
    }
    let result = time >= 0 && time < 10 ? '0' + time : time;
    return result;
  }

  static uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        let r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }
}
